/* eslint-disable react/jsx-no-bind */
import styleUtils from 'shared-components/styles/util.module.css'
import { DSNCPoll, DSPoll, DSTpl } from 'shared-definitions/types'
import Poll, { choicePercent } from 'shared-components/ui/Poll'
import styles from './NCPoll.module.css'
import clsx from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import BasicButton from 'shared-components/buttons/BasicButton'
import { PostContext } from 'shared-components/contexts/PostContext'
import { AppEventsContext } from 'shared-components/contexts/AppEventsContext'
import { AuthContext } from 'shared-components/contexts/AuthContext'
import { usePoll, usePollVote } from 'shared-components/polls/PollStoreContext'
import PollFootnote, {
  removeVoteLabel,
  useCanRemoveVote,
} from 'shared-components/polls/PollFootnote'
import { PollState, pollTotal } from 'shared-components/polls/poll-store'
import DoubleCheck from 'shared-svg/DoubleCheck.svg'
import { ConversationContext } from 'shared-components/comments/conversation/ConversationContext'
import { useConversationPreview } from 'shared-components/comments/conversation/useConversationPreview'
import { SIGN_IN_TO_SAY_WHY as SIGN_IN_TO_SAY_WHY_COPY } from 'shared-components/comments/conversation/have-your-say'
import {
  ASK_LINE,
  campLine,
  POSTED_LINE,
  SAY_WHY_ACTION,
  SIGN_IN_TO_SAY_WHY,
} from 'shared-components/polls/ask-copy'
import { flyPick } from 'shared-components/polls/pick-flight'

interface NCPollProps {
  data: DSNCPoll
  tpl: DSTpl
  requireLoginToShowResults: boolean
}

interface PollVoterProps {
  state: PollState
  showVoteResults: boolean
  poll: DSPoll
  tpl: DSTpl
}

/** The page's poll with the store's live counts (a vote in the conversation panel moves it too). */
function livePoll(poll: DSPoll, state: PollState): DSPoll {
  const byId = new Map(state.choices.map(c => [c.id, c.value]))
  return {
    ...poll,
    items: poll.items.map(c => ({ ...c, value: byId.get(c.id) ?? c.value })),
    votes: pollTotal(state),
  }
}

/** After the pick lands, the chip leaves for the rail card's pick row (`polls/pick-flight`). */
const PICK_FLIGHT_DELAY_MS = 800

/**
 * The post-vote ask under the article's conversation poll (2026-10-28): "Why this one?", the
 * camp's social proof and "Say why →" to the composer — signed out, "Sign in to say why →";
 * once the reader posted with the pick, "Your take is in" and "Read all →". Rendered only after
 * a vote, so its one preview read (shared, cached per container with the rail card's and the
 * bar's) starts then.
 */
const ArticlePollAsk: React.VFC<{ state: PollState; percent: number }> = ({ state, percent }) => {
  const post = PostContext.useOptionalContainer()
  const conversation = ConversationContext.useOptionalContainer()
  const auth = AuthContext.useOptionalContainer()
  const containerId =
    post && !post.sponsored && post.engagementContainerId ? post.engagementContainerId : null
  const { camps } = useConversationPreview(containerId)
  const threads = camps ? camps.find(c => c.choiceId === state.myChoiceId)?.threads ?? 0 : null
  // A vote cast here rises in after the bars and the chip; one read back on load shows at once.
  const late = state.lastVote?.from === 'article'

  const open = (focusComposer: boolean): void => {
    if (conversation?.containerId) {
      conversation.open('article-poll', { focusComposer })
      return
    }
    document.getElementById('comments-section')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }

  if (state.posted) {
    return (
      <p className={clsx(styles.ask, { [styles.askLate]: late })}>
        <strong>{POSTED_LINE}</strong>
        <button type="button" className={styles.askLink} onClick={() => open(false)}>
          {'Read all →'}
        </button>
      </p>
    )
  }
  const signedOut = Boolean(auth && !auth.authState)
  return (
    <p className={clsx(styles.ask, { [styles.askLate]: late })}>
      <strong>{ASK_LINE}</strong>
      <span className={styles.askCamp}>{campLine(threads, percent)}</span>
      <button
        type="button"
        className={styles.askPill}
        onClick={() => {
          if (signedOut && auth) {
            void auth.showLogIn({ copy: SIGN_IN_TO_SAY_WHY_COPY })
            return
          }
          open(true)
        }}
      >
        {`${signedOut ? SIGN_IN_TO_SAY_WHY : SAY_WHY_ACTION} →`}
      </button>
    </p>
  )
}

export const PollVoter: React.VFC<PollVoterProps> = ({ state, showVoteResults, poll, tpl }) => {
  const postMeta = PostContext.useOptionalContainer()
  const vote = usePollVote(poll, 'article', postMeta)
  const canRemove = useCanRemoveVote()
  const shown = useMemo(() => livePoll(poll, state), [poll, state])
  const flight = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (flight.current) {
        clearTimeout(flight.current)
      }
    }
  }, [])

  // The ask: the article's conversation poll only, never in the sidebar; after a vote whose
  // results show (behind the sign-in wall the "Your vote is in!" card asks instead), and not
  // while a removal's Undo shows (the vote is gone).
  const conversationPoll = postMeta?.conversationPoll?.id === poll.id && tpl !== 'sidebar'
  const myChoice = shown.items.find(c => c.id === state.myChoiceId)
  const ask =
    conversationPoll && showVoteResults && myChoice && !state.undo ? (
      <ArticlePollAsk state={state} percent={Number(choicePercent(myChoice.value, shown.votes))} />
    ) : null

  return (
    <Poll
      showVoteResults={showVoteResults}
      choicesClassName={clsx({ [styles.choicesDoubleCols]: tpl !== 'sidebar' })}
      className={clsx(styles.poll, { [styles.submitting]: state.voting })}
      votedId={state.myChoiceId}
      poll={shown}
      onSubmit={choiceId => {
        void vote(choiceId).then(outcome => {
          if (outcome?.kind !== 'voted' || !alive.current) {
            return
          }
          if (flight.current) {
            clearTimeout(flight.current)
          }
          // No-op without the rail card's target (phones) or under reduced motion.
          flight.current = setTimeout(() => flyPick(poll.id), PICK_FLIGHT_DELAY_MS)
        })
      }}
      choiceLabel={(itemId, answer) =>
        removeVoteLabel(answer, itemId === state.myChoiceId, canRemove)
      }
      footer={
        <>
          {ask}
          <PollFootnote poll={poll} state={state} from="article" postMeta={postMeta} />
        </>
      }
    />
  )
}

const NCPoll: React.VFC<NCPollProps> = ({ data, tpl, requireLoginToShowResults }) => {
  const [pageIndex, setPageIndex] = useState(0)
  const page = data.items[pageIndex]
  const hasMore = data.items.length - 1 > pageIndex
  // The one poll store: the reader's choice (localStorage signed out, commentHub signed in) and
  // the counts, shared with the conversation panel's copy of the same poll.
  const state = usePoll(page ?? null)
  const votedId = state?.myChoiceId ?? null
  const { appEvent } = AppEventsContext.useContainer()
  const postMeta = PostContext.useContainer()

  const { authState, showLogIn, showSignUp } = AuthContext.useContainer()
  const authenticated = Boolean(authState)

  function loginTriggered(): void {
    appEvent({
      event: 'poll-login-triggered',
      postMeta,
      question: page.question,
      url: window.location.href,
    })
  }

  return (
    <div className={clsx(styleUtils.nc, styles.container)}>
      {page && state ? (
        <PollVoter
          showVoteResults={
            !requireLoginToShowResults || (requireLoginToShowResults && authenticated)
          }
          state={state}
          poll={page}
          tpl={tpl}
        />
      ) : null}
      {votedId && requireLoginToShowResults && !authenticated ? (
        <div className={styles.loginRequired}>
          <div className={styles.loginRequiredIconHolder}>
            <DoubleCheck className={styles.loginRequiredIcon} />
          </div>
          <h6 className={styles.loginRequiredTitle}>{'Your vote is in!'}</h6>
          <p className={styles.loginRequiredText}>
            {'To reveal the poll results, please '}
            <button
              className={styles.loginRequiredButton}
              type="button"
              onClick={() => {
                loginTriggered()
                void showSignUp()
              }}
            >
              {'sign up'}
            </button>
            {' or '}
            <button
              className={styles.loginRequiredButton}
              type="button"
              onClick={() => {
                loginTriggered()
                void showLogIn()
              }}
            >
              {'log in'}
            </button>
            {'.'}
          </p>
        </div>
      ) : null}
      {data.items.length > 1 ? (
        <BasicButton
          className={clsx(styles.nextButton, {
            [styles.disabled]: !hasMore,
          })}
          onClick={() => setPageIndex(s => s + 1)}
        >
          {'Next question'}
        </BasicButton>
      ) : null}
    </div>
  )
}

export default NCPoll
